import dynamic from 'next/dynamic'
import React, { useMemo } from 'react'

import { NextSeo } from 'next-seo'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'

import AppLayout from '~/components/layouts/AppLayout'
import AdsSlot from '~/components/shared/AdSlot'
import Loader from '~/components/shared/Loader'
import InViewport from '~/containers/InViewport'
import BlogFeaturedSlider from '~/views/Blog/BlogFeaturedSlider/BlogFeaturedSlider'
import BlogHeader from '~/views/Blog/BlogHeader'
import ReviewsSection from '~/views/Blog/Sections/ReviewsSection'

import useIndexBlog, { blogRequest } from '~/hooks/requests/useIndexBlog'
import DFPSlotsProvider from '~/utils/DFPSlotsProvider'

import { formatMessage } from '~/brands'
import { CACHE_KEYS } from '~/constants'
import { BlogBaseArticle } from '~/models/Blog'

const defaultObject: { articles: BlogBaseArticle[] } = {
  articles: []
}

const placeholderFour = new Array(4).fill({}) as BlogBaseArticle[]
const placeholderThree = new Array(3).fill({}) as BlogBaseArticle[]

const adOptions = {
  pageType: 'category',
  pageKey: 'category',
  adUnit: 'news'
}

const DestinationSection = dynamic(
  () => import('../../views/Blog/Sections/DestinationSection'),
  {
    loading: () => <Loader height={500} />,
    ssr: false
  }
)

const NewEventsSection = dynamic(
  () => import('../../views/Blog/Sections/NewEventsSection'),
  {
    loading: () => <Loader height={500} />,
    ssr: false
  }
)

const FeatureSection = dynamic(
  () => import('../../views/Blog/Sections/FeaturedSection'),
  {
    loading: () => <Loader height={500} />,
    ssr: false
  }
)

const Blog = () => {
  const {
    data: [
      reviews = defaultObject,
      destinations = defaultObject,
      news = defaultObject,
      featuredArticles = defaultObject
    ] = [],
    isFetchedAfterMount
  } = useIndexBlog()

  const reviewsLoading = !isFetchedAfterMount && !reviews.articles?.length
  const destinationsLoading =
    !isFetchedAfterMount && !destinations.articles?.length
  const newsLoading = !isFetchedAfterMount && !news.articles?.length
  const featuredArticlesLoading =
    !isFetchedAfterMount && !featuredArticles.articles?.length

  const sliderItems = useMemo(
    () =>
      featuredArticles.articles
        .map((item) => ({
          id: item.id,
          name: item.page_title,
          image: item.primary_image_url,
          category: 'Featured'
        }))
        .slice(0, 3),
    [featuredArticles]
  )

  return (
    <DFPSlotsProvider {...adOptions}>
      <AdsSlot
        renderOutOfThePage
        slotId="gam_oop_pos0"
        sizes={[[1, 1]]}
        targetingArguments={{ pos: '0' }}
        disableSizeMaping
      />
      <AppLayout
        containerProps={{ maxWidth: false, disableGutters: true }}
        disableGutters
      >
        <NextSeo
          title={formatMessage('reviewsNewsPage.meta.title')}
          description={formatMessage('reviewsNewsPage.meta.description')}
          openGraph={{
            title: formatMessage('reviewsNewsPage.meta.title'),
            description: formatMessage('reviewsNewsPage.meta.description')
          }}
        />
        <BlogHeader />
        <BlogFeaturedSlider
          data={sliderItems}
          loading={featuredArticlesLoading}
        />
        <ReviewsSection
          reviewsLoading={reviewsLoading}
          placeholderThree={placeholderThree}
          reviews={reviews}
        />
        <InViewport>
          <DestinationSection
            placeholderFour={placeholderFour}
            destinations={destinations}
            destinationsLoading={destinationsLoading}
          />
          <NewEventsSection
            newsLoading={newsLoading}
            placeholderThree={placeholderThree}
            news={news}
          />
          <FeatureSection
            featuredArticlesLoading={featuredArticlesLoading}
            placeholderFour={placeholderFour}
            featuredArticles={featuredArticles}
          />
        </InViewport>
      </AppLayout>
    </DFPSlotsProvider>
  )
}

export async function getStaticProps() {
  const queryClient = new QueryClient()

  await queryClient.prefetchQuery(CACHE_KEYS.newsReviews, blogRequest)

  return {
    props: {
      dehydratedState: dehydrate(queryClient)
    }
  }
}

export default Blog
